import { supabase } from '@/lib/supabaseClient';
import { useEffect, useRef } from 'react';
import { useUserAlerts } from './useUserAlerts';

// Track which alerts have been processed to avoid duplicates
const processedAlerts = new Set<string>();

// Hook to automatically send webhooks for new alerts
export function useAlertWebhookSender() {
  const { data: alerts } = useUserAlerts();
  const lastProcessedCount = useRef(0);

  useEffect(() => {
    if (!alerts || alerts.length === 0) return;

    // Simple approach: only process new alerts (when count increases)
    const currentCount = alerts.length;
    
    if (currentCount > lastProcessedCount.current) {
      // Find new alerts that need email notifications
      const newCriticalAlerts = alerts
        .filter(alert => 
          !processedAlerts.has(alert.id) && 
          (alert.alert_type === 'delinquent' || 
           alert.alert_type === 'low_credits' || 
           alert.alert_type === 'restored' ||
           alert.alert_type === 'credits_expired' ||
           alert.alert_type === 'credits_expiring_soon' ||
           alert.alert_type === 'call_requires_attention' ||
           alert.alert_type === 'appointment_scheduled' ||
           alert.alert_type === 'appointment_rescheduled' ||
           alert.alert_type === 'appointment_confirmed' ||
           alert.alert_type === 'appointment_cancelled')
        )
        .slice(0, 5); // Process max 5 at a time to avoid overwhelming

      if (newCriticalAlerts.length > 0) {
        newCriticalAlerts.forEach(alert => processedAlerts.add(alert.id));
        sendWebhooksForAlerts(newCriticalAlerts);
      }
      
      lastProcessedCount.current = currentCount;
    }
  }, [alerts]);
}

// Function to send webhooks for multiple alerts
async function sendWebhooksForAlerts(alerts: Array<{id: string, alert_type: string, message: string}>) {
  if (alerts.length === 0) return;


  // Send webhooks with a small delay between each to avoid overwhelming the server
  for (const alert of alerts) {
    try {
      await sendAlertWebhook(alert.id);
      // Small delay between webhook sends
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      // Continue with other alerts even if one fails
    }
  }
}

// Function to send webhook for a single alert
async function sendAlertWebhook(alertId: string) {
  try {
    const { data, error } = await supabase.functions.invoke('send-alert-webhook', {
      body: { alertId }
    });

    if (error) {
      console.error('Error calling send-alert-webhook function:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Failed to send alert webhook:', error);
    throw error;
  }
}

// Function to manually trigger webhooks for all unprocessed important alerts
export async function retryFailedWebhooks() {
  try {
    const { data: alerts } = await supabase
      .from('user_alerts')
      .select('id, alert_type, created_at')
      .in('alert_type', ['delinquent', 'low_credits', 'restored', 'credits_expired', 'credits_expiring_soon', 'call_requires_attention', 'appointment_scheduled', 'appointment_rescheduled', 'appointment_confirmed', 'appointment_cancelled'])
      .order('created_at', { ascending: false })
      .limit(10);

    if (!alerts || alerts.length === 0) {
      console.log('No important alerts found to retry');
      return { success: true, count: 0 };
    }

    
    let successCount = 0;
    for (const alert of alerts) {
      try {
        await sendAlertWebhook(alert.id);
        successCount++;
      } catch (error) {
        console.error(`Failed to retry webhook for alert ${alert.id}:`, error);
      }
    }

    return { 
      success: true, 
      count: successCount, 
      total: alerts.length 
    };
  } catch (error) {
    console.error('Failed to retry webhooks:', error);
    throw error;
  }
} 